import React, { useState, useRef } from 'react'
import { useMutation, gql } from '@apollo/client'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'

import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import {
  Box,
  Typography,
  Input,
  Button,
  Alert,
  Grid,
  Chip,
  IconButton,
  Paper,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CloseIcon from '@mui/icons-material/Close'

// core components
import { createZone, editZone, getZones } from '../../apollo'
import { transformPath, transformPolygon } from '../../utils/coordinates'
import { searchPlaces } from '../../utils/mapbox'
import ConfigurableValues from '../../config/constants'

const CREATE_ZONE = gql`
  ${createZone}
`
const EDIT_ZONE = gql`
  ${editZone}
`
const GET_ZONE = gql`
  ${getZones}
`

const Zone = props => {
  const { MAPBOX_TOKEN } = ConfigurableValues()
  // If editing an existing zone, reconstruct path from stored coordinates
  const [path, setPath] = useState(
    props.zone && props.zone.location?.coordinates?.[0]
      ? transformPolygon(props.zone.location.coordinates[0])
      : []
  )
  const [mutation] = useState(props.zone ? EDIT_ZONE : CREATE_ZONE)
  const [title, setTitle] = useState(props.zone ? props.zone.title : '')
  const [description, setDescription] = useState(
    props.zone ? props.zone.description : ''
  )
  const [errors, setErrors] = useState('')
  const [success, setSuccess] = useState('')
  const [titleError, setTitleError] = useState(null)
  const [descriptionError, setDescriptionError] = useState(null)
  // Multi-city state
  const [locationInput, setLocationInput] = useState('')
  const [selectedCities, setSelectedCities] = useState([])
  // Mapbox place-search dropdown state
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchTimerRef = useRef(null)

  const onCompleted = () => {
    if (!props.zone) clearFields()
    const message = props.zone
      ? t('ZoneUpdatedSuccessfully')
      : t('ZoneAddedSuccessfully')
    setErrors('')
    setSuccess(message)
    setTimeout(hideAlert, 3000)
    if (props.onClose) {
      setTimeout(() => props.onClose(), 4000)
    }
  }

  const onError = error => {
    setErrors(error.message)
    setSuccess('')
    setTimeout(hideAlert, 3000)
  }

  const [mutate] = useMutation(mutation, {
    refetchQueries: [{ query: GET_ZONE }],
    onError,
    onCompleted
  })

  // Compute union bounding box of all selected cities
  const mergeCityPaths = cities => {
    if (cities.length === 0) {
      setPath([])
      return
    }
    const allPoints = cities.flatMap(c => c.path)
    const minLat = Math.min(...allPoints.map(p => p.lat))
    const maxLat = Math.max(...allPoints.map(p => p.lat))
    const minLng = Math.min(...allPoints.map(p => p.lng))
    const maxLng = Math.max(...allPoints.map(p => p.lng))
    setPath([
      { lat: maxLat, lng: minLng }, // NW
      { lat: maxLat, lng: maxLng }, // NE
      { lat: minLat, lng: maxLng }, // SE
      { lat: minLat, lng: minLng }  // SW
    ])
  }

  const removeCity = index => {
    const newCities = selectedCities.filter((_, i) => i !== index)
    setSelectedCities(newCities)
    mergeCityPaths(newCities)
  }

  // Debounced Mapbox forward-geocoding as the user types
  const onLocationInputChange = value => {
    setLocationInput(value)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (!value || value.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchPlaces(value, MAPBOX_TOKEN, { limit: 5 })
        setSuggestions(results)
        setShowSuggestions(results.length > 0)
      } catch (e) {
        setSuggestions([])
        setShowSuggestions(false)
      }
    }, 300)
  }

  // Called when the user selects a place from the suggestions dropdown.
  // Mapbox returns `bbox = [minLng, minLat, maxLng, maxLat]` per result —
  // build the same 4-corner box polygon (NW, NE, SE, SW) the code used to.
  const onPlaceSelected = place => {
    if (!place) return
    const name = place.name || place.placeName || locationInput
    let boxPath = null

    if (place.bbox && place.bbox.length === 4) {
      const [minLng, minLat, maxLng, maxLat] = place.bbox
      boxPath = [
        { lat: maxLat, lng: minLng }, // NW
        { lat: maxLat, lng: maxLng }, // NE
        { lat: minLat, lng: maxLng }, // SE
        { lat: minLat, lng: minLng }  // SW
      ]
    } else if (place.lat != null && place.lng != null) {
      const lat = place.lat
      const lng = place.lng
      const delta = 0.045
      boxPath = [
        { lat: lat + delta, lng: lng - delta },
        { lat: lat + delta, lng: lng + delta },
        { lat: lat - delta, lng: lng + delta },
        { lat: lat - delta, lng: lng - delta }
      ]
    }

    if (!boxPath) return

    const newCities = [...selectedCities, { name, path: boxPath }]
    setSelectedCities(newCities)
    setLocationInput('')
    setSuggestions([])
    setShowSuggestions(false)
    mergeCityPaths(newCities)
  }

  const onSubmitValidation = () => {
    setErrors('')
    const titleErrors = !validateFunc({ title }, 'title')
    const descriptionErrors = !validateFunc({ description }, 'description')

    // When editing an existing zone, the path is already loaded from DB — don't block save
    if (path.length < 3 && !props.zone) {
      setErrors(t('SelectLocationFirst') || 'Please select at least one city or area from the search suggestions')
      return false
    }

    setTitleError(titleErrors)
    setDescriptionError(descriptionErrors)
    return titleErrors && descriptionErrors
  }

  const clearFields = () => {
    setTitle('')
    setDescription('')
    setLocationInput('')
    setSelectedCities([])
    setTitleError(null)
    setDescriptionError(null)
    setPath([])
  }

  const hideAlert = () => {
    setErrors('')
    setSuccess('')
  }

  const { t } = props
  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box
          item
          className={props.zone ? classes.headingBlack : classes.heading}>
          <Typography
            variant="h6"
            className={props.zone ? classes.textWhite : classes.text}>
            {props.zone ? t('EditZone') : t('AddZone')}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.form}>
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Title')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-title"
                  placeholder={t('Title')}
                  type="text"
                  value={title}
                  onChange={event => setTitle(event.target.value)}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    titleError === false
                      ? globalClasses.inputError
                      : titleError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Description')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id="input-description"
                  placeholder={t('Description')}
                  type="text"
                  value={description}
                  onChange={event => setDescription(event.target.value)}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    descriptionError === false
                      ? globalClasses.inputError
                      : descriptionError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
          </Grid>

          {/* City / area search */}
          <Box mt={3}>
            <Typography className={classes.labelText}>
              {t('SearchCityOrArea') || 'Add Cities or Areas to Zone'}
            </Typography>
            <Typography
              variant="body2"
              style={{ color: '#888', marginBottom: 8, fontSize: 12 }}>
              {t('ZoneLocationHint') ||
                'Search and add one or more cities/areas. The zone boundary will automatically expand to cover all selected areas.'}
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
              <Box flex={1} style={{ position: 'relative' }}>
                <input
                  id="input-location"
                  placeholder={t('TypeCityName') || 'e.g. Brooklyn, New York'}
                  type="text"
                  autoComplete="off"
                  value={locationInput}
                  onChange={e => onLocationInputChange(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <Paper
                    style={{
                      position: 'absolute',
                      zIndex: 1300,
                      width: '100%',
                      maxHeight: 240,
                      overflow: 'auto',
                      marginTop: 4,
                      borderRadius: 8,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                    }}>
                    <List dense disablePadding>
                      {suggestions.map(s => (
                        <ListItemButton
                          key={s.id}
                          onMouseDown={() => onPlaceSelected(s)}
                          style={{ paddingTop: 8, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
                          <ListItemText
                            primary={s.name}
                            secondary={s.placeName}
                            primaryTypographyProps={{ fontSize: 13 }}
                            secondaryTypographyProps={{ fontSize: 11 }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
              <Box style={{ paddingTop: 4 }}>
                <AddCircleOutlineIcon
                  style={{ color: '#6FCF97', fontSize: 28, opacity: 0.5 }}
                  titleAccess="Select a city from the dropdown to add it"
                />
              </Box>
            </Box>

            {/* Chips for each selected city */}
            {selectedCities.length > 0 && (
              <Box mt={1.5} display="flex" flexWrap="wrap" gap={0.5}>
                {selectedCities.map((city, index) => (
                  <Chip
                    key={index}
                    icon={<LocationOnIcon style={{ fontSize: 14, color: '#555' }} />}
                    label={city.name}
                    onDelete={() => removeCity(index)}
                    deleteIcon={<CloseIcon style={{ fontSize: 14 }} />}
                    variant="outlined"
                    style={{
                      borderColor: 'green',
                      color: '#333',
                      marginTop: 4
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Combined boundary confirmation */}
            {selectedCities.length > 0 && path.length >= 3 && (
              <Box mt={1} display="flex" alignItems="center" gap={0.5}>
                <CheckCircleOutlineIcon style={{ color: 'green', fontSize: 16 }} />
                <Typography variant="body2" style={{ color: '#555', fontSize: 12 }}>
                  Zone boundary covers <strong>{selectedCities.length}</strong> area{selectedCities.length > 1 ? 's' : ''}
                </Typography>
              </Box>
            )}

            {/* Hint shown when editing an existing zone with no new city selections */}
            {props.zone && selectedCities.length === 0 && path.length >= 3 && (
              <Box mt={1}>
                <Typography
                  variant="body2"
                  style={{ color: '#888', fontSize: 12 }}>
                  Existing zone boundary loaded. Search above to add or replace areas.
                </Typography>
              </Box>
            )}
          </Box>

          <Box mt={3}>
            <Button
              className={globalClasses.button}
              onClick={async e => {
                e.preventDefault()
                if (onSubmitValidation()) {
                  mutate({
                    variables: {
                      zone: {
                        _id: props.zone ? props.zone._id : '',
                        title,
                        description,
                        location: { coordinates: transformPath(path) }
                      }
                    }
                  })
                }
              }}>
              {props.zone ? t('Update') : t('Save')}
            </Button>
          </Box>
        </form>
        <Box mt={2}>
          {success && (
            <Alert
              className={globalClasses.alertSuccess}
              variant="filled"
              severity="success">
              {success}
            </Alert>
          )}
          {errors && (
            <Alert
              className={globalClasses.alertError}
              variant="filled"
              severity="error">
              {errors}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default withTranslation()(Zone)
